import React, { useCallback, useContext, useMemo } from 'react';
import styled from 'styled-components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { DateField as BaseDateField, FlexColumn, FormModal } from '@ubeya/shared-web/components';
import useEmployee from '@ubeya/shared/hooks/employees/useEmployee';
import { getDirtyFields } from '@ubeya/shared/utils/form';
import { validateExpirationDate } from '@ubeya/shared-web/utils/employee';
import UploadEmployeeField from './UploadEmployeeField';

const Name = styled.div`
  color: ${({ theme }) => theme.colors.primary};
  margin-left: 2px;
`;

const TitleWrapper = styled.div`
  display: flex;
`;

const Wrapper = styled(FlexColumn)`
  margin: 20px 0;
  align-items: center;
`;

const DateField = styled(BaseDateField)`
  width: 400px;
`;

const ModalTitle = ({ employee, fieldName }) => (
  <TitleWrapper>
    {fieldName} - <Name>{`${employee.firstName} ${employee.lastName}`}</Name>
  </TitleWrapper>
);

const EditEmployeeFieldModal = ({ refetchCompliance, onClose, data }) => {
  const { t } = useContext(ConfigContext);

  const { employee, name: fieldName, id, value } = data || {};

  const { updateEmployee } = useEmployee({
    employeeId: employee.id
  });

  const fieldValueFormName = useMemo(() => id && `fields.field-${id}`, [id]);

  const fieldExpirationDateFormName = useMemo(() => fieldValueFormName && `${fieldValueFormName}[0].expirationDate`, [
    fieldValueFormName
  ]);

  const handleSubmit = useCallback(
    async (values, form) => {
      const { dirtyFields } = form.getState();
      const dirtyValues = getDirtyFields(values, { ...{ [fieldValueFormName]: dirtyFields[fieldValueFormName] } });

      await updateEmployee(dirtyValues);
      await refetchCompliance?.();
      onClose();
    },
    [onClose, fieldValueFormName, refetchCompliance, updateEmployee]
  );

  const initialValues = useMemo(
    () =>
      id
        ? {
            fields: {
              [`field-${id}`]: value?.link ? [{ ...value, id }] : []
            }
          }
        : {},
    [id, value]
  );

  return (
    <FormModal
      onClose={onClose}
      size="smallLong"
      onSubmit={handleSubmit}
      initialValues={initialValues}
      confirmText="save"
      title={<ModalTitle employee={employee} fieldName={fieldName} />}
      closeOnClickOutside={false}
      isPaddingContent={false}
      usePortal>
      {({ values }) => {
        const fieldData = values?.fields[`field-${id}`];

        const noDocument =
          !fieldData || fieldData.length === 0 || fieldData.every(({ status }) => status === 'deleted');

        return (
          <Wrapper>
            <UploadEmployeeField name={fieldValueFormName} />

            {!noDocument && data?.hasExpiration && (
              <DateField
                showInitialErrorWithoutTouch
                title={t('expirationDate')}
                required
                name={fieldExpirationDateFormName}
                validate={(val) => validateExpirationDate({ value: val, required: true, t })}
                usePortal
              />
            )}
          </Wrapper>
        );
      }}
    </FormModal>
  );
};

export default EditEmployeeFieldModal;
